import { IPostRepository } from '../../domain/repositories/IPostRepository';
import { Post } from '../../domain/entities/Post';
import { ConfigManager } from '../../application/config/Config';
import { LoggerFactory } from '../../application/logging/Logger';
import { InternalServerError } from '../../application/errors/AppError';
import { PostResponse } from '../../types/index';
import { PaginatedPostResponse } from '../../types/index';
import { storageService } from '../../application/services/StorageService';
/**
 * API-based Post Repository Implementation
 * Follows Repository Pattern and Dependency Inversion Principle
 */
export class PostRepository implements IPostRepository {
  private readonly config = ConfigManager.getInstance().getApiConfig();
  private readonly logger = LoggerFactory.getLogger();


  private get token() {
    return storageService.getItem("auth_token");
  }

  // =====================================================
  // 1) FETCH FEEDS
  // =====================================================
  async fetchFeeds(userId: number, coachId: number, page: number): Promise<any[]> {
    const url = `${this.config.baseUrl}/feeds/fetch_feeds`;

    try {
      const body = new URLSearchParams();
      body.append("user_id", String(userId));
      body.append("coach_id", String(coachId));
      body.append("page", String(page));

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Authorization": `Bearer ${this.token}`,
        },
        body,
      });

      const text = await response.text();
      if (!text) return [];

      const json = JSON.parse(text);
      if (!json.data) return [];

      return JSON.parse(json.data) || []; 
    } catch (err) {
      this.logger.error("Failed to fetch feeds", err as Error, { userId, coachId, page });
      return [];
    }
  }

  // =====================================================
  // 2) PUBLISH FEED (post text/photo/video)
  // =====================================================
  async publishFeed(
    userId: number,
    coachId: number,
    postText: string,
    postPhoto: string,
    postVideo: string
  ): Promise<number> {
    const result = await this.publishFeedWithResponse(
      userId,
      coachId,
      postText,
      postPhoto,
      postVideo
    );
    return result.status || 0;
  }

  async publishFeedWithResponse(
    userId: number,
    coachId: number,
    postText: string,
    postPhoto: string,
    postVideo: string
  ): Promise<{ status: number; msg: string; data?: any }> {
    const url = `${this.config.baseUrl}/feeds/publish_feed`;

    try {
      const body = new URLSearchParams();
      body.append("user_id", String(userId));
      body.append("coach_id", String(coachId));
      body.append("post_text", postText);
      body.append("post_photo", postPhoto);
      body.append("post_video", postVideo);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Authorization": `Bearer ${this.token}`,
        },
        body,
      });

      const data = await response.text();
      if (!data) return { status: 0, msg: "Empty response from publish feed API" };

      const json = JSON.parse(data);
      return {
        status: Number(json.status || 0),
        msg: String(json.msg || ""),
        data: json.data,
      };
    } catch (err) {
      this.logger.error("Failed to publish feed", err as Error, { userId, coachId });
      return { status: 0, msg: "Failed to publish feed" };
    }
  }


async deleteFeed(userId: number, feedId: number): Promise<number> {
  const url = `${this.config.baseUrl}/feeds/delete_feed`;

  try {
    const body = new URLSearchParams();
    body.append("user_id", String(userId));
    body.append("feed_id", String(feedId));

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "Authorization": `Bearer ${this.token}`,
      },
      body,
    });

    const text = await response.text();
    if (!text) return 0;

    const json = JSON.parse(text);
    return json.status || 0;
  } catch (err) {
    this.logger.error(
      "Failed to delete feed",
      err as Error,
      { userId, feedId }
    );
    return 0;
  }
}

  // =====================================================
  // 3) UPLOAD FEED IMAGE
  // =====================================================
  async uploadFeedImage(file: File, isThumb = false): Promise<any> {
    const url = `${this.config.baseUrl}/feeds/upload_feed_image`;

    try {
      const form = new FormData();
      form.append("type", isThumb ? "thumb" : "photo");
      form.append("image", file, file.name);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${this.token}`,
        },
        body: form,
      });

      const text = await response.text();
      if (!text) return { status: 0 };

      return JSON.parse(text);
    } catch (err) {
      this.logger.error("Failed to upload feed image", err as Error, { fileName: file.name, isThumb });
      return { status: 0 };
    }
  }

  // =====================================================
  // 4) UPLOAD FEED VIDEO
  // =====================================================
  async uploadFeedVideo(file: File): Promise<any> {
    const url = `${this.config.baseUrl}/feeds/upload_feed_video`;

    try {
      const form = new FormData();
      form.append("video", file, file.name);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${this.token}`,
        },
        body: form,
      });

      const text = await response.text();
      if (!text) return { status: 0 };

      return JSON.parse(text);
    } catch (err) {
      this.logger.error("Failed to upload feed video", err as Error, { fileName: file.name });
      return { status: 0 };
    }
  }

  // =====================================================
  // 5) FETCH COMMENTS
  // =====================================================
  async fetchComments(teamPostId: number, feedId: number, page: number): Promise<any[]> {
    const url = `${this.config.baseUrl}/comments/fetch_comments`;

    try {
      const body = new URLSearchParams();
      body.append("post_id", String(teamPostId));
      body.append("feed_id", String(feedId));
      body.append("page", String(page));

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Authorization": `Bearer ${this.token}`,
        },
        body,
      });

      const text = await response.text();
      if (!text) return [];

      const json = JSON.parse(text);
      if (!json.data) return [];

      return JSON.parse(json.data) || [];
    } catch (err) {
      this.logger.error("Failed to fetch comments", err as Error, { teamPostId, feedId, page });
      return [];
    }
  }

  // =====================================================
  // 6) PUBLISH COMMENT
  // =====================================================
  async publishComment(
    teamPostId: number,
    feedId: number,
    userId: number,
    coachId: number,
    text: string,
    photo: string,
    video: string
  ): Promise<number> {
    const url = `${this.config.baseUrl}/comments/publish_comment`;

    try {
      const body = new URLSearchParams();
      body.append("post_id", String(teamPostId));
      body.append("feed_id", String(feedId));
      body.append("user_id", String(userId));
      body.append("coach_id", String(coachId));
      body.append("post_text", text);
      body.append("post_photo", photo);
      body.append("post_video", video);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Authorization": `Bearer ${this.token}`,
        },
        body,
      });

      const json = JSON.parse(await response.text());
      return json.status || 0;
    } catch (err) {
      this.logger.error("Failed to publish comment", err as Error, { teamPostId, feedId, userId, coachId });
      return 0;
    }
  }

  // =====================================================
  // 7) UPLOAD COMMENT IMAGE
  // =====================================================
  async uploadCommentImage(file: File, isThumb = false): Promise<any> {
    const url = `${this.config.baseUrl}/comments/upload_comment_image`;

    try {
      const form = new FormData();
      form.append("type", isThumb ? "thumb" : "photo");
      form.append("image", file, file.name);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${this.token}`,
        },
        body: form,
      });

      const text = await response.text();
      if (!text) return { status: 0 };

      return JSON.parse(text);
    } catch (err) {
      this.logger.error("Failed to upload comment image", err as Error, { fileName: file.name, isThumb });
      return { status: 0 };
    }
  }


async uploadCommentVideo(file: File): Promise<any> {
    const url = `${this.config.baseUrl}/comments/upload_comment_video`;

    try {
      const form = new FormData();
      form.append("video", file, file.name);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${this.token}`,
        },
        body: form,
      });

      const text = await response.text();
      if (!text) return { status: 0 };

      return JSON.parse(text);
    } catch (err) {
      this.logger.error("Failed to upload comment video", err as Error, { fileName: file.name });
      return { status: 0 };
    }
  }

async deleteFeedPost(postId: number): Promise<boolean> {
  const url = `${this.config.baseUrl}/feeds/delete_feed`;

  try {
    const body = new URLSearchParams();
    body.append("feed_id", String(postId));

    const token = storageService.getItem("auth_token");

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Bearer ${token}`,
      },
      body,
    });

    const text = await response.text();
    if (!text) return false;

    const json = JSON.parse(text);
    return json.status === 1; // success
  } catch (err) {
    this.logger.error("Failed to delete feed post", err as Error, { postId });
    return false;
  }
}

  async sharePracticeFeed(
    practice: {
      practice_id: string;
      practice_date: string;
      practice_time: string;
      CourseTypeLookupId: number;
      createdAt: string;
    },
    practiceImageData: string,
    laps: any[],
    postText: string,
    postPhoto: string,
    postVideo: string,
    userId: number,
    coachUserId: number
  ): Promise<any> {
    try {
      const formData = new FormData();

      formData.append("user_id", userId.toString());
      formData.append("coach_user_id", coachUserId.toString());
      formData.append("practice_id", practice.practice_id.toString());
      formData.append("practice_date", practice.practice_date);
      formData.append("practice_time", practice.practice_time);
      formData.append("practice_CourseTypeLookupId", practice.CourseTypeLookupId.toString());
      formData.append("practice_createdAt", practice.createdAt);
      formData.append("practice_imageData", practiceImageData);

      formData.append("post_text", postText || "");
      formData.append("post_photo", postPhoto || "");
      formData.append("post_video", postVideo || "");

      formData.append("laps", JSON.stringify(laps));

      this.logger.debug("Sharing practice feed", { practiceId: practice.practice_id, userId, coachUserId });

      const response = await fetch(`${this.config.baseUrl}/feeds/share_practice_feed`, {
        method: "POST",
        headers: {
          "X-API-Key": this.config.apiKey,
          "Authorization": `Bearer ${storageService.getItem("auth_token")}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError("Failed to share practice feed");
      }

      return await response.json();
    } catch (error) {
      this.logger.error("Failed to share practice feed", error as Error, { practiceId: practice.practice_id });
      throw error;
    }
  }

async downloadFeedPractice(feedPracticeId: number): Promise<{
  practice: any | null;
  laps: any[];
}> {
  const url = `${this.config.baseUrl}/feeds/download_feed_practice`;

  try {
    const body = new URLSearchParams();
    body.append("feed_practice_id", String(feedPracticeId));

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "Authorization": `Bearer ${this.token}`,
      },
      body,
    });

    const text = await response.text();
    if (!text) return { practice: null, laps: [] };

    const json = JSON.parse(text);

    if (!json || json.status <= 0) {
      return { practice: null, laps: [] };
    }

    const p = json.data.practice;
    const lapsJson = json.data.laps || [];

    // Practice object
    const practice = {
      practice_id: p.practice_id,
      CourseTypeLookupId: p.CourseTypeLookupId,
      practice_date: p.practice_date,
      practice_time: p.practice_time,
      createdAt: p.createdAt,
      isFinished: false,
      imageData: p.imageData,
      name: p.name || "",
      description: p.description || "",
    };

    // Map laps
    const laps = lapsJson.map((lap: any) => ({
      practice_lap_id: lap.practice_lap_id,
      practice_id: lap.practice_id,
      rep: lap.rep,
      distance: lap.distance,
      interval: lap.interval_lap,
      energy_zone: lap.energy_zone,
      description: lap.description,
      time: lap.time,
      createdAt: lap.createdAt,
      passedRepNumber: lap.passedRepNumber,
      isNoEditable: false,
    }));

    return { practice, laps };
  } catch (err) {
    this.logger.error("Failed to download feed practice", err as Error, { feedPracticeId });
    return { practice: null, laps: [] };
  }
}


  /**
   * Retrieves a post record by its ID
   * @param id - The unique identifier of the post record
   * @returns Post domain entity or null if not found
   * @throws InternalServerError if the fetch operation fails
   */
  async findById(id: string): Promise<Post | null> {
    try {
      const response = await fetch(`${this.config.baseUrl}/posts/${id}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new InternalServerError('Failed to fetch post');
      }

      const data = await response.json();
      return Post.fromData(data);
    } catch (error) {
      this.logger.error('Failed to find post by ID', error as Error, { postId: id });
      throw error;
    }
  }

  /**
   * Retrieves all post records for a specific user
   * @param userId - The unique identifier of the user
   * @returns Array of Post domain entities
   * @throws InternalServerError if the fetch operation fails
   */
  async findByUserId(userId: string): Promise<Post[]> {
    try {
      const response = await fetch(`${this.config.baseUrl}/posts?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch user posts');
      }

       const data: { posts: PostResponse[] } = await response.json();

    return data.posts.map((post) => Post.fromData(post));
    } catch (error) {
      this.logger.error('Failed to find posts by user ID', error as Error, { userId });
      throw error;
    }
  }

  async findPublished(): Promise<Post[]> {
    try {
      const response = await fetch(`${this.config.baseUrl}/posts/published`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch published posts');
      }

       const data: { posts: PostResponse[] } = await response.json();

    return data.posts.map((post) => Post.fromData(post));
    } catch (error) {
      this.logger.error('Failed to find published posts', error as Error);
      throw error;
    }
  }

  /**
   * Creates a new post record
   * @param post - The Post domain entity to save
   * @returns Created Post domain entity with server-generated ID
   * @throws InternalServerError if the save operation fails
   */
  async save(post: Post): Promise<Post> {
    try {
      const response = await fetch(`${this.config.baseUrl}/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(post.toJSON())
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to save post');
      }

      const data = await response.json();
      return Post.fromData(data);
    } catch (error) {
      this.logger.error('Failed to save post', error as Error, { postId: post.id });
      throw error;
    }
  }

  /**
   * Updates an existing post record
   * @param post - The Post domain entity with updated data
   * @returns Updated Post domain entity
   * @throws InternalServerError if the update operation fails
   */
  async update(post: Post): Promise<Post> {
    try {
      const response = await fetch(`${this.config.baseUrl}/posts/${post.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(post.toJSON())
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to update post');
      }

      const data = await response.json();
      return Post.fromData(data);
    } catch (error) {
      this.logger.error('Failed to update post', error as Error, { postId: post.id });
      throw error;
    }
  }

  /**
   * Deletes a post record by its ID
   * @param id - The unique identifier of the post record to delete
   * @throws InternalServerError if the delete operation fails
   */
  async delete(id: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/posts/${id}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to delete post');
      }
    } catch (error) {
      this.logger.error('Failed to delete post', error as Error, { postId: id });
      throw error;
    }
  }

  async publish(postId: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/posts/${postId}/publish`, {
        method: 'PUT',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to publish post');
      }
    } catch (error) {
      this.logger.error('Failed to publish post', error as Error, { postId });
      throw error;
    }
  }

  async unpublish(postId: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/posts/${postId}/unpublish`, {
        method: 'PUT',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to unpublish post');
      }
    } catch (error) {
      this.logger.error('Failed to unpublish post', error as Error, { postId });
      throw error;
    }
  }

  async feature(postId: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/posts/${postId}/feature`, {
        method: 'PUT',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to feature post');
      }
    } catch (error) {
      this.logger.error('Failed to feature post', error as Error, { postId });
      throw error;
    }
  }

  async unfeature(postId: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/posts/${postId}/unfeature`, {
        method: 'PUT',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to unfeature post');
      }
    } catch (error) {
      this.logger.error('Failed to unfeature post', error as Error, { postId });
      throw error;
    }
  }

  async like(postId: string, userId: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/posts/${postId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ user_id: userId })
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to like post');
      }
    } catch (error) {
      this.logger.error('Failed to like post', error as Error, { postId, userId });
      throw error;
    }
  }

  async unlike(postId: string, userId: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/posts/${postId}/unlike`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ user_id: userId })
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to unlike post');
      }
    } catch (error) {
      this.logger.error('Failed to unlike post', error as Error, { postId, userId });
      throw error;
    }
  }

  async view(postId: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/posts/${postId}/view`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to view post');
      }
    } catch (error) {
      this.logger.error('Failed to view post', error as Error, { postId });
      throw error;
    }
  }

  async search(query: string): Promise<Post[]> {
    try {
      const params = new URLSearchParams({ search: query });

      const response = await fetch(`${this.config.baseUrl}/posts/search?${params}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to search posts');
      }

       const data: { posts: PostResponse[] } = await response.json();

    return data.posts.map((post) => Post.fromData(post));
    } catch (error) {
      this.logger.error('Failed to search posts', error as Error, { query });
      throw error;
    }
  }

  async findByCategory(category: string): Promise<Post[]> {
    try {
      const response = await fetch(`${this.config.baseUrl}/posts/category/${category}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch posts by category');
      }

       const data: { posts: PostResponse[] } = await response.json();

    return data.posts.map((post) => Post.fromData(post));
    } catch (error) {
      this.logger.error('Failed to find posts by category', error as Error, { category });
      throw error;
    }
  }

  async findByTags(tags: string[]): Promise<Post[]> {
    try {
      const params = new URLSearchParams({ tags: tags.join(',') });

      const response = await fetch(`${this.config.baseUrl}/posts/tags?${params}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch posts by tags');
      }

      const data: { posts: PostResponse[] } = await response.json();

    return data.posts.map((post) => Post.fromData(post));
    } catch (error) {
      this.logger.error('Failed to find posts by tags', error as Error, { tags });
      throw error;
    }
  }

  async findFeatured(): Promise<Post[]> {
    try {
      const response = await fetch(`${this.config.baseUrl}/posts/featured`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch featured posts');
      }

     const data: { posts: PostResponse[] } = await response.json();

    return data.posts.map((post) => Post.fromData(post));
    } catch (error) {
      this.logger.error('Failed to find featured posts', error as Error);
      throw error;
    }
  }

  async findTrending(limit: number): Promise<Post[]> {
    try {
      const params = new URLSearchParams({ limit: limit.toString() });

      const response = await fetch(`${this.config.baseUrl}/posts/trending?${params}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch trending posts');
      }

 const data: { posts: PostResponse[] } = await response.json();

    return data.posts.map((post) => Post.fromData(post));
    } catch (error) {
      this.logger.error('Failed to find trending posts', error as Error, { limit });
      throw error;
    }
  }

  async getPostStats(postId: string): Promise<{
    likes: number;
    views: number;
    comments: number;
    engagementRate: number;
  }> {
    try {
      const response = await fetch(`${this.config.baseUrl}/posts/${postId}/stats`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch post statistics');
      }

      const data = await response.json();
      return {
        likes: data.likes || 0,
        views: data.views || 0,
        comments: data.comments || 0,
        engagementRate: data.engagement_rate || 0
      };
    } catch (error) {
      this.logger.error('Failed to get post statistics', error as Error, { postId });
      throw error;
    }
  }

  async findPaginated(
  userId: string,
  page: number,
  limit: number
): Promise<{
  posts: Post[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}> {
  try {
    const params = new URLSearchParams({
      user_id: userId,
      page: page.toString(),
      limit: limit.toString(),
    });

    const response = await fetch(`${this.config.baseUrl}/posts?${params}`, {
      method: "GET",
      headers: {
        "X-API-Key": this.config.apiKey,
        Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
      },
    });

    if (!response.ok) {
      throw new InternalServerError("Failed to fetch paginated posts");
    }

    const data: PaginatedPostResponse = await response.json();

    return {
      posts: data.posts.map((post) => Post.fromData(post)),
      total: data.total,
      page: data.page,
      limit: data.limit,
      totalPages: data.total_pages,
    };
  } catch (error) {
    this.logger.error("Failed to get paginated posts", error as Error, {
      userId,
      page,
      limit,
    });
    throw error;
  }
}
}
